import type { TableRuntimeBillingGroup, TableRuntimePhysicalTable, TableRuntimeSync } from "@/api/business/tableRuntime"
import type { MerchantOpeningTableDTO } from "@/api/business/types/table"
import { TableStatusConstants } from "@/types/tableStatusConstants"
import { normalizeOpaqueLongId } from "@/utils/opaqueLongId"

type Result = { kind: "applied" | "ignored" | "refresh", tables?: MerchantOpeningTableDTO[] }
const refresh = (): Result => ({ kind: "refresh" })
const safeRevision = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0
const validId = (value: unknown): value is string => typeof value === "string" && normalizeOpaqueLongId(value) === value

/** 新协议只更新展示值；记录或业务版本改变时先重新读取操作权限、会员、备注等元数据。 */
export class TableRuntimeProjection {
  private cursor?: { shopId: string, epoch: string, sequence: number, catalog: number }
  private groups = new Map<string, TableRuntimeBillingGroup>()
  private tables = new Map<string, TableRuntimePhysicalTable>()
  private clock?: { server: number, received: number }

  reset() {
    this.cursor = undefined
    this.groups.clear()
    this.tables.clear()
    this.clock = undefined
  }

  accept(raw: unknown, shopId: string, metadata: MerchantOpeningTableDTO[], freshMetadata = false, now = Date.now()): Result {
    try {
      return this.acceptEnvelope(raw as TableRuntimeSync, shopId, metadata, freshMetadata, now)
    } catch {
      return refresh()
    }
  }

  private acceptEnvelope(value: TableRuntimeSync, shopId: string, metadata: MerchantOpeningTableDTO[], fresh: boolean, now: number): Result {
    if (!value || value.protocolVersion !== 1 || value.shopId !== shopId
      || value.readStatus !== "AVAILABLE" || value.readSource !== "PROJECTION"
      || value.projectionState !== "READY" || !value.cursorUsable || value.stale || value.requiresSnapshot
      || !value.projectionEpoch || !safeRevision(value.shopSequence)
      || !safeRevision(value.serverTime) || value.unresolvedTableIds.length || value.unresolvedTableRecordIds.length
      || (value.staleAt !== null && (!safeRevision(value.staleAt) || value.staleAt <= value.serverTime))) return refresh()
    const full = value.scope === "FULL" && value.messageType === "SNAPSHOT"
    const incremental = value.scope === "INCREMENTAL" && value.messageType === "REPLACED"
    if (!full && !incremental) return refresh()
    const serverNow = incremental && this.clock
      ? Math.max(value.serverTime, this.clock.server + Math.max(0, now - this.clock.received))
      : value.serverTime
    if (value.staleAt !== null && value.staleAt <= serverNow) return refresh()
    const cursor = this.cursor
    if (incremental && (!cursor || cursor.shopId !== shopId || cursor.epoch !== value.projectionEpoch)) return refresh()
    if (cursor?.shopId === shopId && cursor.epoch === value.projectionEpoch) {
      if (value.shopSequence < cursor.sequence || (incremental && value.shopSequence === cursor.sequence)) return { kind: "ignored" }
      if (incremental && value.shopSequence !== cursor.sequence + 1) return refresh()
      if (!fresh && value.catalogVersion !== null && value.catalogVersion !== cursor.catalog) return refresh()
    }
    if (full && (!safeRevision(value.catalogVersion) || (!fresh && !cursor))) return refresh()
    const groups = full ? new Map<string, TableRuntimeBillingGroup>() : new Map(this.groups)
    const tables = full ? new Map<string, TableRuntimePhysicalTable>() : new Map(this.tables)
    const seen = new Set<string>()
    for (const group of value.groups) {
      if (!validId(group.aggregateKey) || seen.has(group.aggregateKey) || group.dataStatus !== "LIVE"
        || !safeRevision(group.aggregateVersion) || !safeRevision(group.timeRevision)) return refresh()
      seen.add(group.aggregateKey)
      const previous = this.groups.get(group.aggregateKey)
      if (!fresh && (!previous || previous.aggregateVersion !== group.aggregateVersion
        || previous.timeRevision > group.timeRevision || previous.status !== group.status)) return refresh()
      groups.set(group.aggregateKey, group)
    }
    for (const tombstone of value.aggregateTombstones) {
      if (!validId(tombstone.aggregateKey) || seen.has(tombstone.aggregateKey) || !safeRevision(tombstone.aggregateVersion)) return refresh()
      groups.delete(tombstone.aggregateKey)
    }
    const tableIds = new Set<string>()
    for (const table of value.tables) {
      if (!validId(table.tableId) || tableIds.has(table.tableId) || table.dataStatus !== "LIVE"
        || !safeRevision(table.runtimePayableAmount)) return refresh()
      const previous = this.tables.get(table.tableId)
      if (!fresh && (!previous || ["reservation", "tableName", "categoryId", "capacity", "sort", "reservable"].some(key =>
        JSON.stringify(previous[key as keyof TableRuntimePhysicalTable]) !== JSON.stringify(table[key as keyof TableRuntimePhysicalTable])))) return refresh()
      tableIds.add(table.tableId)
      tables.set(table.tableId, table)
    }
    if (incremental && [...tables.values()].some(table => table.currentAggregateKey
      && seen.has(table.currentAggregateKey) && !tableIds.has(table.tableId))) return refresh()
    if (incremental && [...groups.values()].some(group => group.nextChangeAt != null
      && (!safeRevision(group.nextChangeAt) || group.nextChangeAt <= serverNow))) return refresh()
    // 缺表、新开台、并台释放都需要旧元数据同步完成，不能拼接另一笔消费的会员/备注。
    if (tables.size !== metadata.length) return refresh()
    const result: MerchantOpeningTableDTO[] = []
    for (const item of metadata) {
      const table = tables.get(normalizeOpaqueLongId(item.tableId) ?? "")
      if (!table || (item.shopId && normalizeOpaqueLongId(item.shopId) !== shopId)) return refresh()
      const status = TableStatusConstants[table.physicalStatus as keyof typeof TableStatusConstants]
      if (!status || status !== item.status
        || (normalizeOpaqueLongId(item.tableRecordId) ?? null) !== table.currentTableRecordId) return refresh()
      if (table.currentTableRecordId !== null) {
        const group = groups.get(table.currentAggregateKey ?? "")
        const member = group?.members.find(entry => entry.tableRecordId === table.currentTableRecordId)
        if (!group || !member || group.aggregateVersion !== table.currentAggregateVersion
          || member.sourceTableId !== table.tableId) return refresh()
        result.push({ ...item, totalFee: table.runtimePayableAmount })
      } else {
        if (table.currentAggregateKey !== null || table.runtimePayableAmount !== 0) return refresh()
        result.push({ ...item, totalFee: 0 })
      }
    }
    this.groups = groups
    this.tables = tables
    this.clock = { server: serverNow, received: now }
    this.cursor = { shopId, epoch: value.projectionEpoch, sequence: value.shopSequence, catalog: value.catalogVersion ?? cursor!.catalog }
    return { kind: "applied", tables: result }
  }
}
